/**
 * Browser polyfills for Node.js modules
 * 
 * This module provides polyfills for Node.js built-in modules when running in the browser.
 * It's needed for libraries that assume Node.js environment but are used in browser context.
 */

import { Buffer } from 'buffer';
import streamBrowserify from 'stream-browserify';

// Import type definitions
/// <reference path="../types/global.d.ts" />
/// <reference path="../types/module-declarations.d.ts" />

/**
 * Interface for our browser-compatible Process object
 */
interface BrowserProcess {
  env: {
    NODE_ENV: string;
    PUBLIC_URL: string;
    [key: string]: string | undefined;
  };
  browser?: boolean;
  version?: string;
  title?: string;
  release?: {
    name: string;
  };
  nextTick?: (callback: (...args: any[]) => void, ...args: any[]) => void;
}

/**
 * Initialize all browser polyfills
 */
export function initPolyfills(): void {
  // Only run in browser context
  if (typeof window === 'undefined') return;

  // Add Buffer to window
  if (!(window as any).Buffer) {
    (window as any).Buffer = Buffer;
    console.log('Buffer polyfill initialized');
  }

  // Add stream to window
  if (!(window as any).stream) {
    (window as any).stream = streamBrowserify;
    console.log('Stream polyfill initialized');
  }

  // Add process to window with proper typing
  if (!(window as any).process) {
    // Create browser-compatible process object
    const browserProcess: BrowserProcess = {
      env: {
        NODE_ENV: typeof process !== 'undefined' && process.env ? process.env.NODE_ENV || 'production' : 'production',
        PUBLIC_URL: typeof process !== 'undefined' && process.env ? process.env.PUBLIC_URL || '' : '',
        REACT_APP_VERSION: typeof process !== 'undefined' && process.env ? process.env.REACT_APP_VERSION || '' : '',
        REACT_APP_BUILD_TIME: typeof process !== 'undefined' && process.env ? process.env.REACT_APP_BUILD_TIME || new Date().toISOString() : new Date().toISOString()
      },
      browser: true,
      version: '1.0.0',
      title: 'browser',
      release: {
        name: 'browser'
      },
      nextTick: (callback: (...args: any[]) => void, ...args: any[]) => {
        setTimeout(() => callback(...args), 0);
      }
    };

    (window as any).process = browserProcess;
    console.log('Process polyfill initialized');
  }
}

// Auto-initialize polyfills
initPolyfills();